/**
 * Gravity bed — warm low root + fifth under the cloud.
 *
 * Presence follows field strength and clump. Tight clumps drift toward
 * unison (centered, calm); open bowls keep a soft open fifth.
 * Pure sines only — a felt foundation, never a growl or buzz.
 */

import type { SimParams } from "../../sim/params";
import type { AudioVoiceContext, AudioVoicePlugin } from "../../plugins/types";
import type { AudioFeatureFrame } from "../features";
import { clamp01, intervalHz, midiToHz, softGain, TAU } from "../music";
import type { AudioParams } from "../params";

export function mapGravityDrone(
  frame: AudioFeatureFrame,
  audio: AudioParams,
): { gain: number; rootHz: number; fifthHz: number; subHz: number } {
  if (!frame.gravity.modeActive) {
    return {
      gain: 0,
      rootHz: midiToHz(33),
      fifthHz: midiToHz(40),
      subHz: midiToHz(21),
    };
  }
  const g = frame.gravity;
  const clump = clamp01(g.clump);
  // Quiet bed: strength opens the gate, clump sweetens it
  const amp = g.strengthNorm * (0.35 + 0.65 * clump);
  // Near the well → a touch more presence (still soft)
  const near = 1 - clamp01(g.meanWellDist / 1.2);
  const gain = softGain(amp * (0.75 + 0.25 * near), 0.1);

  // Register around A1–E2; pitchAmount allows a small lift
  const lift = audio.pitchAmount * 5 * g.strengthNorm * (0.4 + 0.6 * (1 - clump));
  const rootHz = midiToHz(33 + lift); // ~A1…
  // Clump → fifth drifts toward root (more unison / calmer bowl)
  const fifthSemis = 7 - 2.5 * clump;
  const fifthHz = intervalHz(rootHz, fifthSemis);
  const subHz = rootHz * 0.5;
  return { gain, rootHz, fifthHz, subHz };
}

type Nodes = {
  root: OscillatorNode;
  fifth: OscillatorNode;
  sub: OscillatorNode;
  master: GainNode;
};

function createGravityDrone(): AudioVoicePlugin {
  let nodes: Nodes | null = null;
  return {
    id: "gravity_drone",
    label: "Gravity bed",
    tier: "lab",
    enabled(audio: AudioParams, _sim: SimParams) {
      return audio.enabled && audio.gravityVoice;
    },
    start(acx: AudioVoiceContext, _audio: AudioParams) {
      if (nodes) return;
      const { ctx, dest } = acx;
      const master = ctx.createGain();
      master.gain.value = 0;
      // Soft lowpass so the bed never gets buzzy on small screens/speakers
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 420;
      filter.Q.value = 0.3;
      filter.connect(master);
      master.connect(dest);

      const mk = (hz: number, level: number) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = hz;
        const g = ctx.createGain();
        g.gain.value = level;
        osc.connect(g);
        g.connect(filter);
        osc.start();
        return osc;
      };

      const root = mk(midiToHz(33), 0.5);
      const fifth = mk(midiToHz(40), 0.32);
      const sub = mk(midiToHz(21), 0.18);
      nodes = { root, fifth, sub, master };
    },
    process(frame: AudioFeatureFrame, audio: AudioParams, t: number) {
      if (!nodes) return;
      const m = mapGravityDrone(frame, audio);
      nodes.master.gain.setTargetAtTime(m.gain, t, TAU.gain);
      nodes.root.frequency.setTargetAtTime(m.rootHz, t, TAU.chord);
      nodes.fifth.frequency.setTargetAtTime(m.fifthHz, t, TAU.chord);
      nodes.sub.frequency.setTargetAtTime(m.subHz, t, TAU.chord);
    },
    stop(t: number) {
      if (!nodes) return;
      try {
        nodes.master.gain.setTargetAtTime(0, t, 0.28);
        nodes.root.stop(t + 0.8);
        nodes.fifth.stop(t + 0.8);
        nodes.sub.stop(t + 0.8);
      } catch {
        /* */
      }
      nodes = null;
    },
  };
}

export const gravityDroneVoice: AudioVoicePlugin = createGravityDrone();
